import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  applyPayment,
  confirmLoanCardPayment,
  createLoanCardPaymentIntent,
} from '../../endpoints/loans';
import { getApiErrorMessage } from '../../utils/httpClientTs';
import {
  StorefrontStripePaymentSection,
  stripePromise,
  type StripeCheckoutSession,
} from '../ChapterSuppliesStorefront/StorefrontStripePaymentForm';

type PaymentMethod = 'check' | 'card' | 'on_file';

type ElfLoanPaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  borrowerUserId: string;
  currency?: string;
  onSuccess: () => void;
};

const ElfLoanPaymentDialog: React.FC<ElfLoanPaymentDialogProps> = ({
  open,
  onClose,
  borrowerUserId,
  currency = 'USD',
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('check');
  const [amount, setAmount] = useState('');
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [checkNumber, setCheckNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [cardSession, setCardSession] = useState<StripeCheckoutSession | null>(
    null
  );
  const [cardIntentPending, setCardIntentPending] = useState(false);

  const resetForm = () => {
    setPaymentMethod('check');
    setAmount('');
    setPostingDate(new Date().toISOString().slice(0, 10));
    setCheckNumber('');
    setError(null);
    setPending(false);
    setCardSession(null);
    setCardIntentPending(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setCardSession(null);
    setError(null);
  }, [paymentMethod]);

  const parsedAmount = Number(amount);
  const canPay = parsedAmount > 0 && postingDate.trim() !== '';

  const handleClose = () => {
    if (pending || cardIntentPending) return;
    onClose();
  };

  const handleSubmitCheck = async () => {
    setError(null);
    const ref = checkNumber.trim();
    if (!ref) {
      setError('Check or reference number is required.');
      return;
    }
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    setPending(true);
    try {
      await applyPayment({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
        payment_method: 'check',
        payment_ref: ref,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handlePrepareCard = async () => {
    setError(null);
    setCardSession(null);
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (!stripePromise) {
      setError('Stripe is not configured for card payments.');
      return;
    }
    setCardIntentPending(true);
    try {
      const data = await createLoanCardPaymentIntent({
        user_id: borrowerUserId,
        amount: parsedAmount,
      });
      setCardSession({
        order_id: borrowerUserId,
        payment_intent_id: data.payment_intent_id,
        payment_client_secret: data.client_secret,
      });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCardIntentPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make payment</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payment applies across this borrower&apos;s active loans ({currency}).
          Requires loan:manage permission.
        </Typography>

        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            value={paymentMethod}
            onChange={(_, value) => value && setPaymentMethod(value)}
            size="small"
            fullWidth
          >
            <ToggleButton value="check">Check</ToggleButton>
            <ToggleButton value="card">Card (phone)</ToggleButton>
            <Tooltip title="Coming soon — vault and PayPal">
              <span>
                <ToggleButton value="on_file" disabled>
                  Account on file
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>

          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0.01, step: 0.01 }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <TextField
            label="Posting date"
            type="date"
            size="small"
            fullWidth
            value={postingDate}
            onChange={(e) => setPostingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {paymentMethod === 'check' && (
            <TextField
              label="Check or reference number"
              size="small"
              fullWidth
              required
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
            />
          )}

          {paymentMethod === 'card' && (
            <>
              {!stripePromise && (
                <Alert severity="error">
                  Stripe is not configured for card payments.
                </Alert>
              )}
              {!cardSession && (
                <Button
                  variant="contained"
                  disabled={!canPay || !stripePromise || cardIntentPending}
                  onClick={handlePrepareCard}
                >
                  {cardIntentPending ? 'Preparing…' : 'Continue to card entry'}
                </Button>
              )}
              {cardSession && stripePromise && (
                <StorefrontStripePaymentSection
                  checkoutSession={cardSession}
                  submitLabel="Complete payment"
                  confirmPayment={async ({ payment_intent_id }) => {
                    const result = await confirmLoanCardPayment({
                      user_id: borrowerUserId,
                      payment_intent_id,
                      posting_date: postingDate,
                    });
                    return {
                      order_status_id: result.payment_id,
                      order_status_name: 'paid',
                    };
                  }}
                  onError={(msg) => setError(msg)}
                  onSuccess={() => {
                    onSuccess();
                    onClose();
                  }}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={pending || cardIntentPending}>
          Cancel
        </Button>
        {paymentMethod === 'check' && (
          <Button
            variant="contained"
            disabled={!canPay || pending}
            onClick={handleSubmitCheck}
          >
            {pending ? 'Applying…' : 'Apply payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ElfLoanPaymentDialog;
